import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { saveToOffline, getFromOffline, removeFromOffline, getAllOffline } from '../lib/offlineDb';

const OfflineContext = createContext();

export const OfflineProvider = ({ children }) => {
    const [offlineMaterials, setOfflineMaterials] = useState([]);
    const [isSlowConnection, setIsSlowConnection] = useState(false);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);

    const refreshOfflineList = useCallback(async () => {
        try {
            const all = await getAllOffline();
            setOfflineMaterials(all || []);
        } catch (e) {
            console.error('Failed to load offline materials', e);
        }
    }, []);

    useEffect(() => {
        refreshOfflineList();

        // Connection speed detection
        const checkConnection = () => {
            const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
            if (conn) {
                // effectiveType can be 'slow-2g', '2g', '3g', or '4g'
                setIsSlowConnection(['slow-2g', '2g'].includes(conn.effectiveType) || conn.saveData);
            }
            setIsOffline(!navigator.onLine);
        };

        checkConnection();

        window.addEventListener('online', checkConnection);
        window.addEventListener('offline', checkConnection);

        if (navigator.connection) {
            navigator.connection.addEventListener('change', checkConnection);
        }

        return () => {
            window.removeEventListener('online', checkConnection);
            window.removeEventListener('offline', checkConnection);
            if (navigator.connection) {
                navigator.connection.removeEventListener('change', checkConnection);
            }
        };
    }, [refreshOfflineList]);

    const saveForOffline = async (material) => {
        try {
            // If it's a PDF, we might want to pre-fetch the blob to actually work offline
            if (material.file_url && material.type === 'pdf') {
                const response = await fetch(material.file_url);
                const blob = await response.blob();
                material.offline_blob = blob;
            }

            await saveToOffline(material);
            await refreshOfflineList();
            return true;
        } catch (e) {
            console.error('Save for offline failed', e);
            return false;
        }
    };

    const removeOffline = async (id) => {
        await removeFromOffline(id);
        await refreshOfflineList();
    };

    const isMaterialOffline = (id) => {
        return offlineMaterials.some(m => m.id === id);
    };

    return (
        <OfflineContext.Provider value={{
            offlineMaterials,
            isSlowConnection,
            isOffline,
            saveForOffline,
            removeOffline,
            isMaterialOffline,
            refreshOfflineList
        }}>
            {children}
        </OfflineContext.Provider>
    );
};

export const useOffline = () => useContext(OfflineContext);
